import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DeckSummary, getDeck, listDecks } from '../api/decks';
import SlideThumbnail, { SLIDE_THUMB_ASPECT_RATIO } from '../components/SlideThumbnail';
import { Button, ButtonLink } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { canUseAi } from '../data/subscriptions';
import { usePlanLabel } from '../i18n/usePlanLabel';
import { saveSlides } from '../data/slidesStore';
import { saveDesignId } from '../data/designStore';
import { saveActiveDeck } from '../data/deckStore';
import { getDesignOrDefault } from '../data/designTemplates';
import { appTheme, pitchTheme } from '../styles/theme';

const Page = styled.main`
  flex: 1;
  padding: 48px 24px 64px;
  background: ${appTheme.pageBg};
`;

const Container = styled.div`
  max-width: ${appTheme.maxWidth};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  margin-right: auto;
  font-family: ${pitchTheme.fontPageTitle};
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 500;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${appTheme.greenDark};
`;

const PlanBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 20px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  font-size: 0.875rem;
  color: ${appTheme.text};

  strong {
    color: ${appTheme.greenDark};
  }
`;

const DeckGrid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
`;

const DeckCard = styled.li`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border-radius: ${appTheme.radiusLg};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.surface};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${appTheme.green};
    box-shadow: ${appTheme.shadowMd};
    transform: translateY(-2px);
  }
`;

const DeckThumbFallback = styled.div<{ $gradient: string }>`
  width: 100%;
  aspect-ratio: ${SLIDE_THUMB_ASPECT_RATIO};
  border-radius: ${appTheme.radiusMd};
  background: ${({ $gradient }) => $gradient};
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 10px 12px;
`;

const SlideCountBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fff;
  background: rgba(10, 46, 40, 0.45);
  border-radius: ${appTheme.radiusPill};
  padding: 4px 10px;
`;

const DeckName = styled.h2`
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const DeckMeta = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${appTheme.textMuted};
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
`;

const ActionButton = styled(Button)`
  flex: 1;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 24px;
  border-radius: ${appTheme.radiusLg};
  border: 1px dashed ${appTheme.border};
  background: ${appTheme.surface};
  text-align: center;
`;

const EmptyTitle = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  color: ${appTheme.text};
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${appTheme.textMuted};
  max-width: 36ch;
`;

const StatusMessage = styled.p<{ $error?: boolean }>`
  margin: 0;
  padding: 12px 16px;
  border-radius: ${appTheme.radiusMd};
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ $error }) => ($error ? appTheme.dangerText : appTheme.textMuted)};
  background: ${({ $error }) => ($error ? appTheme.dangerBg : appTheme.surfaceHover)};
  border: 1px solid ${({ $error }) => ($error ? appTheme.dangerBorder : appTheme.border)};
`;

const formatDate = (iso: string, locale: string): string =>
  new Date(iso).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const DecksPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const planName = usePlanLabel(user?.plan ?? null);
  const [decks, setDecks] = useState<DeckSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listDecks()
      .then((result) => {
        if (!cancelled) {
          setDecks(result);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openDeck = async (deckId: string, destination: '/preview' | '/edit') => {
    setOpeningId(deckId);
    try {
      const deck = await getDeck(deckId);
      saveSlides(deck.slides);
      saveDesignId(deck.designId);
      saveActiveDeck(deck.id, deck.name, deck.designId, deck.logoUrl);
      navigate(destination, { state: { from: '/decks' } });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t('decks.couldNotOpen'));
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <Page>
      <Container>
        <Header>
          <Title>{t('decks.title')}</Title>
          <ButtonLink to="/decks/new" $variant="primary" $size="md">
            {t('decks.createNew')}
          </ButtonLink>
        </Header>

        <PlanBanner>
          <span>
            {t('decks.currentPlan')} <strong>{planName}</strong>
            {!canUseAi(user?.plan) && t('decks.upgradeHint')}
          </span>
          <ButtonLink to="/subscription" $variant="secondary" $size="sm">
            {canUseAi(user?.plan) ? t('common.managePlan') : t('common.upgradePlan')}
          </ButtonLink>
        </PlanBanner>

        {error && <StatusMessage $error>{error}</StatusMessage>}
        {!error && decks === null && <StatusMessage>{t('decks.loading')}</StatusMessage>}
        {!error && decks !== null && decks.length === 0 && (
          <EmptyState>
            <EmptyTitle>{t('decks.emptyTitle')}</EmptyTitle>
            <EmptyText>{t('decks.emptyText')}</EmptyText>
            <ButtonLink to="/decks/new" $variant="primary">
              {t('decks.createNew')}
            </ButtonLink>
          </EmptyState>
        )}

        {!error && decks !== null && decks.length > 0 && (
          <DeckGrid>
            {decks.map((deck) => {
              const design = getDesignOrDefault(deck.designId);
              return (
                <DeckCard key={deck.id}>
                  {deck.firstSlide ? (
                    <SlideThumbnail slide={deck.firstSlide} palette={design.palette} />
                  ) : (
                    <DeckThumbFallback $gradient={design.palette.gradient}>
                      <SlideCountBadge>
                        {t('decks.slideCount', { count: deck.slideCount })}
                      </SlideCountBadge>
                    </DeckThumbFallback>
                  )}
                  <DeckName>{deck.name}</DeckName>
                  <DeckMeta>
                    {t('decks.slideCount', { count: deck.slideCount })} · {t('decks.updated')}{' '}
                    {formatDate(deck.updatedAt, i18n.language)}
                  </DeckMeta>
                  <CardActions>
                    <ActionButton
                      type="button"
                      $variant="primary"
                      disabled={openingId === deck.id}
                      onClick={() => openDeck(deck.id, '/preview')}
                    >
                      {t('common.present')}
                    </ActionButton>
                    <ActionButton
                      type="button"
                      $variant="secondary"
                      disabled={openingId === deck.id}
                      onClick={() => openDeck(deck.id, '/edit')}
                    >
                      {t('common.edit')}
                    </ActionButton>
                  </CardActions>
                </DeckCard>
              );
            })}
          </DeckGrid>
        )}
      </Container>
    </Page>
  );
};

export default DecksPage;
